/**
 * i18n Service
 * Handles internationalization configuration and initialization
 */

// #region Imports
import i18n from "i18next";
import ICU from "i18next-icu";
import { initReactI18next } from "react-i18next";

// Import language resources
import enApi from "./resources/en/api.json";
import enComponents from "./resources/en/components.json";
import enCommon from "./resources/en/common.json";
import enForms from "./resources/en/forms.json";
import enLanding from "./resources/en/landing.json";
import enValidation from "./resources/en/validation.json";
import enLegal from "./resources/en/legal.json";

import roApi from "./resources/ro/api.json";
import roComponents from "./resources/ro/components.json";
import roCommon from "./resources/ro/common.json";
import roForms from "./resources/ro/forms.json";
import roLanding from "./resources/ro/landing.json";
import roValidation from "./resources/ro/validation.json";
import roLegal from "./resources/ro/legal.json";
// #endregion

// #region Types
/**
 * Supported languages in the application
 */
export type SupportedLanguage = "en" | "ro";

/**
 * Language configuration interface
 */
export interface LanguageConfig {
    code: SupportedLanguage;
    name: string;
    nativeName: string;
    flag: string;
}

/**
 * i18n resources structure
 */
export interface Resources {
    api: typeof enApi;
    components: typeof enComponents;
    common: typeof enCommon;
    forms: typeof enForms;
    landing: typeof enLanding;
    validation: typeof enValidation;
    legal: typeof enLegal;
}
// #endregion

// #region Constants
/**
 * Available language configurations
 */
export const LANGUAGE_CONFIGS: Record<SupportedLanguage, LanguageConfig> = {
    en: {
        code: "en",
        name: "English",
        nativeName: "English",
        flag: "🇺🇸",
    },
    ro: {
        code: "ro",
        name: "Romanian",
        nativeName: "Română",
        flag: "🇷🇴",
    },
} as const;

/**
 * Default language
 */
export const DEFAULT_LANGUAGE: SupportedLanguage = "ro";

/**
 * Language resources
 */
const resources = {
    en: {
        api: enApi,
        components: enComponents,
        common: enCommon,
        forms: enForms,
        landing: enLanding,
        validation: enValidation,
        legal: enLegal,
    },
    ro: {
        api: roApi,
        components: roComponents,
        common: roCommon,
        forms: roForms,
        landing: roLanding,
        validation: roValidation,
        legal: roLegal,
    },
} as const;
// #endregion

// #region Service
/**
 * i18n Service class
 * Handles internationalization configuration and utilities
 */
export class I18nService {
    /**
     * Initialize i18n configuration
     */
    static initialize(): void {
        i18n.use(ICU)
            .use(initReactI18next)
            .init({
                resources,
                lng: DEFAULT_LANGUAGE,
                fallbackLng: DEFAULT_LANGUAGE,
                interpolation: {
                    escapeValue: false, // React already does escaping
                },
                defaultNS: "common",
                ns: ["api", "components", "common", "forms", "landing", "validation", "legal"],
            });
    }

    /**
     * Get available languages
     */
    static getAvailableLanguages(): LanguageConfig[] {
        return Object.values(LANGUAGE_CONFIGS);
    }

    /**
     * Get language configuration by code
     */
    static getLanguageConfig(code: SupportedLanguage): LanguageConfig {
        return LANGUAGE_CONFIGS[code];
    }

    /**
     * Check if language is supported
     */
    static isSupportedLanguage(code: string): code is SupportedLanguage {
        return Object.keys(LANGUAGE_CONFIGS).includes(code);
    }

    /**
     * Get current language from i18n instance
     */
    static getCurrentLanguage(): SupportedLanguage {
        const currentLang = i18n.language;
        return this.isSupportedLanguage(currentLang) ? currentLang : DEFAULT_LANGUAGE;
    }

    /**
     * Change language
     */
    static async changeLanguage(language: SupportedLanguage): Promise<void> {
        await i18n.changeLanguage(language);
    }
}
// #endregion

// #region Initialization
// Initialize i18n when the service is imported
I18nService.initialize();
// #endregion

// #region Exports
export default i18n;
// #endregion
